/* eslint-disable boundaries/element-types */
import { FilterProps } from 'components/table/enhancements/filters/filterTypes'
import {
  getSortedUniqueValues,
  removeFilter,
  useClickOutside,
} from 'components/table/enhancements/filters/filterUtils'
import {
  FilterContainer,
  FilterHeaderButton,
  RelativeWrap,
  RemoveFilterButton,
  StyledCloseIcon,
  StyledInput,
} from 'features/assetDatabase'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ComboboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  min-width: 200px;
`

const ComboboxList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  width: 100%;
`

const ComboboxOption = styled.div<{ $isSelected: boolean }>`
  padding: 8px;
  cursor: pointer;
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.forecast.hoverButton : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledFilterContainer = styled(FilterContainer)`
  z-index: 1000;
  min-width: 200px;
`

export const ComboboxFilter = <TData,>({
  column,
  columnHeader,
  setActiveFilters,
  navigate,
  disableUrlFilters,
}: FilterProps<TData>) => {
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isInputVisible, setInputVisible] = useState(false)
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const columnFilterValue = column.getFilterValue() as string
  const isFirstRender = useRef(true)

  // Handle initial render and filter value changes
  useEffect(() => {
    if (isFirstRender.current) {
      // On first render, if there's no filter value, it means we're adding a new filter
      if (!columnFilterValue) {
        setInputVisible(true)
      } else {
        setCurrentFilterValue(columnFilterValue)
      }
      isFirstRender.current = false
    } else if (columnFilterValue) {
      // For subsequent updates, if there's a filter value, keep input hidden
      setInputVisible(false)
      setCurrentFilterValue(columnFilterValue)
    }
  }, [columnFilterValue])

  const sortedUniqueValues = React.useMemo(
    () => getSortedUniqueValues(column),
    [column]
  )

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return sortedUniqueValues
    return sortedUniqueValues.filter((value) =>
      value?.toLowerCase().includes(searchTerm?.toLowerCase())
    )
  }, [searchTerm, sortedUniqueValues])

  const handleOptionSelect = (value: string) => {
    setCurrentFilterValue(value)
    column.setFilterValue(value)

    // Update URL parameters (only if URL filters are enabled)
    if (!disableUrlFilters) {
      navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          [column.id]: value,
        }),
      })
    }
    setInputVisible(false)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleOutsideClick = () => {
    if (!currentFilterValue) {
      removeFilter(column, setActiveFilters, navigate)
    } else {
      setInputVisible(false)
    }
  }

  const handleRemoveFilter = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent the FilterHeaderButton click from firing
    setCurrentFilterValue('')
    setSearchTerm('')
    column.setFilterValue(undefined)
    removeFilter(column, setActiveFilters, navigate)
  }

  useClickOutside(filterContainerRef, handleOutsideClick)

  useEffect(() => {
    if (isInputVisible && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isInputVisible])

  return (
    <RelativeWrap>
      <FilterHeaderButton
        onClick={() => {
          setInputVisible(true)
          setSearchTerm('') // Reset search term when opening
          // Focus input immediately after state update
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus()
            }
          }, 0)
        }}>
        {columnHeader}
        {currentFilterValue && (
          <RemoveFilterButton onClick={handleRemoveFilter}>
            {currentFilterValue}
            <StyledCloseIcon />
          </RemoveFilterButton>
        )}
      </FilterHeaderButton>
      {isInputVisible && (
        <StyledFilterContainer
          ref={filterContainerRef}
          className={isInputVisible ? 'input-visible' : ''}>
          <ComboboxContainer>
            <StyledInput
              ref={inputRef}
              type='text'
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={`Search ${columnHeader.toLowerCase()}...`}
            />
            <ComboboxList>
              {filteredOptions.map((value) => (
                <ComboboxOption
                  key={value}
                  $isSelected={value === currentFilterValue}
                  onClick={() => handleOptionSelect(value)}>
                  {value}
                </ComboboxOption>
              ))}
            </ComboboxList>
          </ComboboxContainer>
        </StyledFilterContainer>
      )}
    </RelativeWrap>
  )
}
